import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Crown, Lock, ArrowRight, CheckCircle, AlertTriangle, Zap, TrendingUp, Search, MapPin, Star, BarChart2, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const ScoreRing = ({ score, size = 100 }) => {
  const r = (size / 2) - 8;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const color = score >= 70 ? '#22c55e' : score >= 50 ? '#D4A017' : '#ef4444';
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="8"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2 + 6} textAnchor="middle" fill={color} fontSize={size > 80 ? 20 : 14} fontWeight="700">{score}</text>
    </svg>
  );
};

const LockedCard = ({ title, icon: Icon }) => (
  <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-6 relative overflow-hidden">
    <div className="absolute inset-0 bg-[#0a0f1e]/80 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-2xl">
      <div className="w-12 h-12 bg-[#D4A017]/10 border border-[#D4A017]/20 rounded-full flex items-center justify-center mb-3">
        <Lock className="w-5 h-5 text-[#D4A017]" />
      </div>
      <p className="text-white font-semibold text-sm mb-1">Vergrendeld</p>
      <p className="text-[#64748b] text-xs text-center mb-3">Ontgrendel met IMPERIA AI Radar</p>
      <Link to="/radar" className="bg-[#D4A017] text-[#0f172a] font-bold text-xs px-4 py-2 rounded-lg hover:bg-[#B8860B] transition-all">
        Ontgrendelen — €14,95 excl. btw
      </Link>
    </div>
    <div className="flex items-center gap-3 mb-4 opacity-30">
      <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
        {Icon && <Icon className="w-4 h-4 text-white" />}
      </div>
      <h3 className="text-white font-bold text-sm">{title}</h3>
    </div>
    <div className="space-y-2 opacity-20">
      {[...Array(4)].map((_, i) => (
        <div key={i} className={`h-3 bg-white/30 rounded ${i === 1 ? 'w-4/5' : i === 2 ? 'w-3/5' : 'w-full'}`} />
      ))}
    </div>
  </div>
);

export default function ScanDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try URL token first (direct link from email)
    const urlToken = searchParams.get('token');
    if (urlToken) {
      localStorage.setItem('portal_token', urlToken);
    }

    const token = localStorage.getItem('portal_token');
    if (!token) { navigate('/scan'); return; }

    // Redirect naar het nieuwe /platform dashboard
    navigate('/platform', { replace: true });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    navigate('/login');
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#64748b]">Dashboard laden...</p>
      </div>
    </div>
  );

  const scan = data?.scan;
  const analyse = scan?.analyse || {};
  const company = data?.company;
  const isPaid = data?.is_paid;
  const score = analyse.overall_score || 0;
  const modules = analyse.module_scores || {};

  const moduleList = [
    { key: 'website', label: 'Website', icon: BarChart2 },
    { key: 'seo', label: 'SEO', icon: Search },
    { key: 'lokale_zichtbaarheid', label: 'Lokale Zichtbaarheid', icon: MapPin },
    { key: 'reputatie', label: 'Reputatie & Reviews', icon: Star },
    { key: 'advertenties', label: 'Advertenties', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4A017] via-[#f5c842] to-[#D4A017]" />

      {/* Topbar */}
      <header className="bg-[#0f172a] border-b border-white/5 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#D4A017] rounded-lg flex items-center justify-center">
              <Crown className="w-5 h-5 text-[#0f172a]" />
            </div>
            <span className="text-white font-bold font-serif hidden sm:block">IMPERIA AI</span>
          </Link>
          <div className="flex items-center gap-4">
            {!isPaid && (
              <Link to="/radar" className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold text-sm px-4 py-2 rounded-lg transition-all">
                Upgrade — €14,95 excl. btw
              </Link>
            )}
            <button onClick={handleLogout} className="text-[#64748b] hover:text-white transition-colors">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Welcome + Score */}
        <div className="grid md:grid-cols-3 gap-5">
          {/* Score card */}
          <div className="md:col-span-1 bg-[#0f172a] rounded-2xl border border-white/5 p-6 flex flex-col items-center justify-center text-center">
            <p className="text-[#64748b] text-xs uppercase tracking-wider mb-3">Online Groeiscore</p>
            <ScoreRing score={score} size={120} />
            <p className="text-white font-bold text-lg mt-3">{analyse.score_label || 'Matig'}</p>
            <p className="text-[#64748b] text-xs mt-1 max-w-[160px]">{analyse.score_toelichting}</p>
          </div>

          {/* Top kans */}
          <div className="md:col-span-2 bg-gradient-to-br from-[#D4A017]/20 to-[#D4A017]/5 border border-[#D4A017]/30 rounded-2xl p-6">
            <p className="text-[#D4A017] text-xs font-bold uppercase tracking-wider mb-3">Uw grootste groeikans</p>
            <h1 className="text-white font-bold text-xl mb-4 leading-snug">{analyse.top_kans || 'Verbeter uw online aanwezigheid'}</h1>
            <p className="text-[#94a3b8] text-sm mb-5">{analyse.persoonlijk_advies}</p>
            <div className="flex gap-3">
              <Link to="/radar" className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-5 py-2.5 rounded-xl text-sm transition-all flex items-center gap-2">
                Volledig inzicht — €14,95 excl. btw <ArrowRight size={14} />
              </Link>
              <Link to="/offerte" className="bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-all">
                Laten uitvoeren
              </Link>
            </div>
          </div>
        </div>

        {/* Kritieke punten + Quick wins */}
        <div className="grid md:grid-cols-2 gap-5">
          {/* Kritieke punten */}
          <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h2 className="text-white font-bold">3 Kritieke aandachtspunten</h2>
            </div>
            <div className="space-y-3">
              {(analyse.kritieke_punten || []).map((p, i) => (
                <div key={i} className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white font-semibold text-sm">{p.titel}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.impact === 'Hoog' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'}`}>{p.impact}</span>
                  </div>
                  <p className="text-[#64748b] text-sm">{p.beschrijving}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quick wins */}
          <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-[#D4A017]" />
              <h2 className="text-white font-bold">3 Quick wins</h2>
            </div>
            <div className="space-y-3">
              {(analyse.quick_wins || []).map((w, i) => (
                <div key={i} className="bg-[#D4A017]/5 border border-[#D4A017]/20 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white font-semibold text-sm">{w.titel}</p>
                    <span className="text-xs bg-[#D4A017]/20 text-[#D4A017] px-2 py-0.5 rounded-full">{w.tijdsinvestering}</span>
                  </div>
                  <p className="text-[#64748b] text-sm">{w.beschrijving}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Module scores */}
        <div>
          <h2 className="text-white font-bold mb-4">Analyse per module</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {moduleList.map(({ key, label, icon: Icon }) => {
              const mod = modules[key] || {};
              return isPaid ? (
                <div key={key} className="bg-[#0f172a] rounded-2xl border border-white/5 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-4 h-4 text-[#D4A017]" />
                    <p className="text-white font-semibold text-sm">{label}</p>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <ScoreRing score={mod.score || 0} size={48} />
                    <p className="text-[#64748b] text-xs">{mod.label}</p>
                  </div>
                  <p className="text-[#94a3b8] text-xs">{mod.preview}</p>
                </div>
              ) : (
                <div key={key} className="bg-[#0f172a] rounded-2xl border border-white/5 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-4 h-4 text-[#D4A017]" />
                    <p className="text-white font-semibold text-sm">{label}</p>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <ScoreRing score={mod.score || 0} size={48} />
                    <p className="text-[#64748b] text-xs">{mod.label}</p>
                  </div>
                  <p className="text-[#94a3b8] text-xs">{mod.preview}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Locked sections */}
        {!isPaid && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-5 h-5 text-[#D4A017]" />
              <h2 className="text-white font-bold">Vergrendelde analyses</h2>
              <span className="bg-[#D4A017]/20 text-[#D4A017] text-xs px-3 py-1 rounded-full font-bold">IMPERIA AI Radar</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <LockedCard title="Volledige SEO-analyse & zoekwoorden" icon={Search} />
              <LockedCard title="Concurrentiepositie & benchmarks" icon={BarChart2} />
              <LockedCard title="Lokale directory status" icon={MapPin} />
              <LockedCard title="Review monitoring & reputatiescore" icon={Star} />
              <LockedCard title="Maandelijkse groeirapportage" icon={TrendingUp} />
              <LockedCard title="Stap-voor-stap verbeterinstructies" icon={CheckCircle} />
            </div>
          </div>
        )}

        {/* Upgrade CTA */}
        {!isPaid && (
          <div className="bg-gradient-to-br from-[#D4A017]/20 via-[#0f172a] to-[#0f172a] border border-[#D4A017]/30 rounded-2xl p-8 text-center">
            <p className="text-[#D4A017] text-xs font-bold uppercase tracking-widest mb-3">IMPERIA AI Radar</p>
            <h2 className="text-white font-bold text-2xl mb-3">Ontgrendel alle inzichten voor {company?.name}</h2>
            <p className="text-[#64748b] mb-6 max-w-xl mx-auto">
              Ontvang maandelijks complete analyses, stap-voor-stap verbeterinstructies en de mogelijkheid om alles door IMPERIA AI te laten uitvoeren.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              {['Volledige analyses','Maandelijkse monitoring','Verbeterinstructies','Uitvoeropties IMPERIA AI'].map(f => (
                <div key={f} className="flex items-center gap-2 text-sm text-white">
                  <CheckCircle className="w-4 h-4 text-[#D4A017]" /> {f}
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/radar" className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-8 py-4 rounded-xl text-lg transition-all">
                Start Connected Self-Service — €14,95 excl. btw <ArrowRight className="inline ml-2" size={18} />
              </Link>
              <Link to="/radar?plan=year" className="bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all">
                Jaar — €99,95 (bespaar €20)
              </Link>
            </div>
            <p className="text-[#475569] text-xs mt-4">Geen lange contracten · Altijd opzegbaar · Gratis te starten</p>
          </div>
        )}
      </div>
    </div>
  );
}
